import Analytics from 'analytics';
import segmentPlugin from '@analytics/segment';
import find from 'lodash/find';
import sumBy from "lodash/sumBy";

const urlsMap = [
    {
        regexp: '/login',
        name: 'Login',
        category: 'Authentication'
    },
    {
        regexp: '/register',
        name: 'Register',
        category: 'Authentication'
    },
    {
        regexp: '/password/reset',
        name: 'Forgot Password - Request',
        category: 'Authentication'
    },
    {
        regexp: '/password/reset/([^/]*)',
        name: 'Forgot Password - Change',
        category: 'Authentication'
    },
    {
        regexp: '/login/2fa/token',
        name: 'Login - 2FA',
        category: 'Authentication'
    },
    {
        regexp: '/docs',
        name: 'Docs - Main',
        category: 'Documentation'
    },
    {
        regexp: '/docs/api',
        name: 'Docs - API',
        category: 'Documentation'
    },
    {
        regexp: '/thank-you/([^/]*)',
        name: 'Thank you page',
        category: 'Hosted'
    },
    {
        regexp: '/f/([^/]*)/preview',
        name: 'Hosted Form Preview',
        category: 'Hosted'
    },
    {
        regexp: '/f/([^/]*)',
        name: 'Hosted Form Live',
        category: 'Hosted'
    },
    {
        regexp: '/teams/([^/]*)',
        name: 'Team Home',
        category: 'Team'
    },
    {
        regexp: '/teams/([^/]*)/members',
        name: 'Team Settings',
        category: 'Team'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)',
        name: 'Form Overview',
        category: 'Form'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)/inbox',
        name: 'Form Submissions',
        category: 'Form'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)/settings',
        name: 'Form Settings',
        category: 'Form'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)/spam-settings',
        name: 'Form Spam Settings',
        category: 'Form'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)/integrations',
        name: 'Form Integrations',
        category: 'Form'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)/notifications',
        name: 'Form Notifications',
        category: 'Form'
    },
    {
        regexp: '/teams/([^/]*)/forms/([^/]*)/builder',
        name: 'Form Builder',
        category: 'Form'
    },
    {
        regexp: '/profile',
        name: 'User Profile',
        category: 'User'
    },
    {
        regexp: '/billing',
        name: 'User Billing',
        category: 'User'
    },
    {
        regexp: '/teams/([^/]*)/subscription',
        name: 'Team Subscription',
        category: 'Subscription'
    },
    {
        regexp: '/teams/([^/]*)/subscription/change',
        name: 'Subscription',
        category: 'Team Select Subscription'
    },
];

function getMatchedPage() {
    const path = document.location.pathname;
    return find(urlsMap, url => {
        const regx = new RegExp('^' + url.regexp + '$');
        return regx.test(path);
    });
}

function prepareProduct(product) {
    return {
        product_id: product.id,
        sku: product.currentPrice.id,
        name: product.original_name,
        price: product.currentPrice.cost > 0 ? parseFloat(product.currentPrice.cost / 100) : 0,
        quantity: 1,
        currency: product.currentPrice.currency.toUpperCase()
    }
}

export default {
    // eslint-disable-next-line no-unused-vars
    install(Vue, options) {
        Vue.prototype.$analytics = Analytics({
            app: 'headless-forms',
            plugins: [
                segmentPlugin({
                    writeKey: process.env.MIX_SEGMENT_FRONTEND_KEY
                })
            ]
        });
        Vue.prototype.$analytics.trackCustomPageView = function () {
            let match = getMatchedPage();
            this.page(match ? {
                name: match.name,
                category: match.category
            } : {});
        }
        Vue.prototype.$analytics.identifyUser = function (user, team) {
            this.identify((user ? user.id.toString() : ''), {});
            if (user && team) {
                this.ready(() => {
                    window.analytics.group(team.id, {
                        name: team.name,
                        plan: team.subscription_title,
                        priceId: team.subscription_price,
                        created_at: team.team_created_at,
                        subscribed_at: team.subscription_started_at
                    })
                });
            }
        }
        Vue.prototype.$analytics.trackProductsListView = function (period, productsList) {
            this.track('Product List Viewed', {
                category: period,
                products: _.map(productsList, prepareProduct)
            })
        }
        Vue.prototype.$analytics.trackProductsAdded = function (teamId, product) {
            let payload = prepareProduct(product);
            payload.cart_id = teamId;
            this.track('Product Added', payload)
        }
        Vue.prototype.$analytics.trackCheckoutStarted = function (checkoutId, product) {
            const preparedProduct = prepareProduct(product);
            this.track('Checkout Started', {
                checkout_id: checkoutId,
                order_id: null,
                value: preparedProduct.price,
                revenue: preparedProduct.price,
                products: [preparedProduct]
            });
        }
        Vue.prototype.$analytics.trackOrderCompleted = function (checkoutId, product, invoice, coupon) {
            const preparedProduct = prepareProduct(product),
                total = invoice.total / 100,
                discount = sumBy(invoice.total_discount_amounts, d => d.amount);
            this.track('Order Completed', {
                checkout_id: checkoutId,
                order_id: invoice.id,
                subtotal: total,
                revenue: total,
                discount: discount > 0 ? discount / 100 : 0,
                coupon: coupon ? coupon.id : null,
                currency: invoice.currency.toUpperCase(),
                products: [preparedProduct]
            });
        }
        Vue.prototype.$analytics.trackOrderCanceled = function (checkoutId, product, coupon) {
            const preparedProduct = prepareProduct(product),
                total = (coupon.isValid && coupon.invoice) ? coupon.invoice.total / 100 : preparedProduct.price,
                discount = (coupon.isValid && coupon.invoice) ? sumBy(coupon.invoice.total_discount_amounts, d => d.amount) : 0;
            this.track('Order Canceled', {
                checkout_id: checkoutId,
                order_id: null,
                subtotal: total,
                revenue: total,
                discount: discount > 0 ? discount / 100 : 0,
                coupon: coupon.isValid ? coupon.id : null,
                currency: preparedProduct.currency.toUpperCase(),
                products: [preparedProduct]
            });
        }
        Vue.prototype.$analytics.trackCouponEntered = function (checkoutId, promocode) {
            this.track('Coupon Entered', {
                coupon_id: promocode,
                checkout_id: checkoutId
            });
        }
        Vue.prototype.$analytics.trackCouponApplied = function (checkoutId, coupon) {
            const discount = coupon.invoice ? sumBy(coupon.invoice.total_discount_amounts, d => d.amount) / 100 : 0;
            this.track('Coupon Applied', {
                coupon_id: coupon.id,
                checkout_id: checkoutId,
                discount
            });
        }
        Vue.prototype.$analytics.trackCouponDenied = function (checkoutId, promocode) {
            this.track('Coupon Denied', {
                coupon_id: promocode,
                checkout_id: checkoutId
            });
        }
    }
};
